import { toError } from '@fruitful/fruitils';

import { IS_LOCAL } from '../config/FruitfulEnvironment';
import type { AdminUser } from '../entities/entityTypes';
import FetchError from '../http/FetchError';
import DatadogFacade from './datadog/DatadogFacade';
import type { TrackedEvent } from './Events';
import MixpanelFacade from './mixpanel/MixpanelFacade';
import {
    enrichSentryScope,
    type SentryErrorData,
} from './sentry/enrichSentryScope';
import SentryFacade from './sentry/SentryFacade';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ErrorData = Record<string, any>;

export const initLoggers = () => {
    SentryFacade.init();
    DatadogFacade.init();
    MixpanelFacade.init();
};

export const identifyUser = (user: AdminUser) => {
    MixpanelFacade.identifyUser(user);
    SentryFacade.identifyUser(user);
};

export const captureMessage = (message: string) => {
    if (DatadogFacade.isEnabled()) {
        DatadogFacade.Datadog.info(message);
    }

    if (IS_LOCAL) {
        console.info(message);
    }
};

export const captureWarning = (message: string, exception?: unknown) => {
    const error = exception == null ? undefined : toError(exception, message);

    if (DatadogFacade.isEnabled()) {
        DatadogFacade.Datadog.warn(message, error);
    }

    if (IS_LOCAL) {
        console.warn(message, error);
    }
};

export const captureDebug = (message: string) => {
    if (DatadogFacade.isEnabled()) {
        DatadogFacade.Datadog.debug(message);
    }

    if (IS_LOCAL) {
        console.debug(message);
    }
};

const getSentryErrorData = (
    exception: Error,
    data?: ErrorData,
): SentryErrorData | undefined => {
    if (!(exception instanceof FetchError)) {
        return data;
    }

    const existingTags =
        data != null &&
        typeof data.tags === 'object' &&
        data.tags != null &&
        !Array.isArray(data.tags)
            ? (data.tags as Record<string, string | number | boolean>)
            : {};

    return {
        ...data,
        tags: {
            ...existingTags,
            endpoint: exception.endpoint,
            method: exception.method,
            status: exception.status,
        },
    };
};

const getHttpStatusForReporting = (error: Error): number | undefined => {
    if (error instanceof FetchError) {
        return error.status;
    }

    // Mastra SDK throws MastraClientError (name + status).
    if (
        error.name === 'MastraClientError' &&
        'status' in error &&
        typeof error.status === 'number'
    ) {
        return error.status;
    }

    return undefined;
};

/** Skip expected client HTTP failures (auth, not found, validation) for llm-service / Mastra. */
const shouldSkipHttpClientErrorReporting = (error: Error): boolean => {
    const status = getHttpStatusForReporting(error);
    return status != null && status >= 400 && status < 500;
};

export const captureException = (exception: unknown, data?: ErrorData) => {
    const error = toError(exception);

    if (shouldSkipHttpClientErrorReporting(error)) {
        return;
    }

    const sentryData = getSentryErrorData(error, data);

    if (SentryFacade.isEnabled()) {
        SentryFacade.Sentry.withScope((scope) => {
            enrichSentryScope(scope, sentryData);
            SentryFacade.Sentry.captureException(error);
        });
    }

    if (DatadogFacade.isEnabled()) {
        DatadogFacade.logError(error, data);
    }

    if (IS_LOCAL) {
        console.log(error, data);
    }
};

export const handleUncaughtException = (error: unknown, stackTrace: string) => {
    captureException(error, {
        stackTrace,
    });
};

export const trackEvent = (
    event: TrackedEvent,
    data?: Record<string, string | number | boolean>,
    sendImmediately?: boolean,
) => {
    MixpanelFacade.track(event, data, sendImmediately);
};
